/**
 * useDiscounts — Coupons & Promos data hook.
 *
 * Shared cache backed by the shipped `@v0y4ge/api-client` promos ops.
 * `listPromos` hydrates once; mutations AWAIT the client op and commit to the
 * cache only on resolution (UIR-21, Voyage #194/#201 — the old optimistic
 * commit + swallowed `.catch(() => {})` rendered rejected writes as success).
 * Failures reject to callers, which own the error toast.
 */
import { t as i18nT } from "../../lib/i18n";
import { useEffect, useState } from "react";
// Namespace import: the served preview bundles against a STUB @v0y4ge/api-client
// that may not export the promos ops, and a named import would fail the build.
// We resolve each op at runtime and fall back to the seed when absent.
import * as apiClient from "@v0y4ge/api-client";
import { toDiscount, toPromoInput, newDiscount as _newDiscount, newDiscountId, PROMO_LIBRARY, type Discount, type DiscountType } from "./promos-data";
import { logUpsert, logDelete } from "../../lib/audit";
import { can, onTenantChange, onTenantSwitching } from "../../shell/access";

const api = apiClient as Record<string, any>;
const listPromos = api.listPromos as ((p?: any) => Promise<any>) | undefined;
const createPromo = api.createPromo as ((i: any) => Promise<any>) | undefined;
const updatePromo = api.updatePromo as ((id: string, p: any) => Promise<any>) | undefined;
const duplicatePromo = api.duplicatePromo as ((id: string) => Promise<any>) | undefined;
const pausePromo = api.pausePromo as ((id: string, paused?: boolean) => Promise<any>) | undefined;
const deletePromo = api.deletePromo as ((id: string) => Promise<any>) | undefined;

let _cache: Discount[] | null = null;
let _loading = false;
/** Set only when the list read FAILED — never for an empty library (2026-07-31
 *  rule; screens pass this to <LoadState> so an outage cannot read as "no
 *  discounts"). Distinct from the PROMO_LIBRARY fallback, which is also empty. */
let _error: string | null = null;
const _listeners = new Set<() => void>();
const _notify = () => _listeners.forEach((fn) => fn());
const _set = (next: Discount[]) => { _cache = next; _notify(); };
let _serverTotal: number | null = null; // §2.11 — authoritative list total from the response, never rows.length
onTenantSwitching(() => { _cache = null; _loading = false; _error = null; _serverTotal = null; _notify(); });
onTenantChange(() => { if (can("promos.read")) hydrate(); }); // admin/promos — gated (docket 2026-09-17)

function hydrate(done?: () => void) {
  if (_loading) { done?.(); return; }
  _loading = true;
  _error = null;
  // §2.11 (2026-07-26, completed): PromoListQuery declares page+limit and the
  // response carries pagination.total/hasMore — page to exhaustion (10-page /
  // 2,000-row valve) instead of presenting the first page as the whole set.
  (async () => {
    const acc: any[] = [];
    let page = 1;
    let total: number | null = null;
    for (;;) {
      const r: any = typeof listPromos === "function" ? await listPromos({ page, limit: 200 } as any) : null;
      const arr = (r?.discounts ?? r?.data ?? []) as any[];
      acc.push(...arr);
      total = (r?.pagination?.total ?? r?.total ?? null) as number | null;
      const more = !!(r?.pagination?.hasMore ?? r?.hasMore);
      if (!more || arr.length === 0 || page >= 10) break;
      page++;
    }
    _serverTotal = total ?? (acc.length || null);
    // PROMO_LIBRARY is empty (#635) — a stubbed/empty client renders empty.
    _cache = acc.length ? acc.map(toDiscount) : PROMO_LIBRARY.map((d) => ({ ...d }));
    _loading = false; _notify(); done?.();
  })()
    .catch((e) => { console.warn("listPromos failed:", e); _error = "Couldn't load discounts."; _cache = PROMO_LIBRARY.map((d) => ({ ...d })); _loading = false; _notify(); done?.(); });
}

export function useDiscounts() {
  const [, force] = useState(0);
  const [loading, setLoading] = useState(_cache === null);

  useEffect(() => {
    const fn = () => force((n) => n + 1);
    _listeners.add(fn);
    if (_cache === null && !_loading) hydrate(() => setLoading(false));
    else setLoading(false);
    return () => { _listeners.delete(fn); };
  }, []);

  const all = _cache ?? [];
  return {
    loading,
    /** Failed read — pass to <LoadState>; never render an empty list over this. */
    error: _error,
    /** §2.11: server-reported list total (null when the op doesn't report one). */
    serverTotal: _serverTotal,
    all: (): Discount[] => all,
    ofType: (t: DiscountType): Discount[] => all.filter((d) => d.type === t),
    approved: (): Discount[] => all, // admin-authored — all are selectable
    get: (id: string): Discount | null => all.find((d) => d.id === id) ?? null,
    newDiscount: _newDiscount,
    newId: newDiscountId,
    upsert: async (d: Discount): Promise<Discount> => {
      const idx = all.findIndex((x) => x.id === d.id);
      const existed = idx >= 0;
      const draft = { ...d, updated: "Just now" };
      const input = toPromoInput(draft);
      let saved = draft;
      if (existed) {
        if (typeof updatePromo !== "function") throw new Error("updatePromo is unavailable in this build — nothing was saved.");
        const real = toDiscount(await updatePromo(d.id, input as any));
        if (real.id) saved = real;
      } else {
        if (typeof createPromo !== "function") throw new Error("createPromo is unavailable in this build — nothing was saved.");
        const real = toDiscount(await createPromo(input as any));
        if (real.id) saved = real;
      }
      logUpsert("promo", existed ? (all[idx] as unknown as Record<string, unknown>) : null, saved as unknown as Record<string, unknown>, { name: d.name });
      _set(existed ? (_cache ?? []).map((x) => (x.id === d.id ? saved : x)) : [saved, ...(_cache ?? [])]);
      return saved;
    },
    duplicate: async (id: string): Promise<Discount | null> => {
      const src = all.find((x) => x.id === id);
      if (!src) return null;
      if (typeof duplicatePromo !== "function") throw new Error("duplicatePromo is unavailable in this build — no copy was created.");
      const real = toDiscount(await duplicatePromo(id));
      const copy: Discount = real.id ? real : {
        ...src, id: newDiscountId("d"), name: i18nT("promos.useDiscounts.srcNameCopy.icu", "{value0} (copy)", { value0: src.name }), status: "draft",
        uses: 0, owner: "You", updated: "Just now",
        code: src.type === "coupon" && src.code ? `${src.code}2` : src.code,
      };
      _set([copy, ...(_cache ?? [])]);
      return copy;
    },
    setStatus: async (id: string, status: Discount["status"]) => {
      const d = all.find((x) => x.id === id);
      if (!d) return;
      if (typeof updatePromo !== "function") throw new Error("updatePromo is unavailable in this build — nothing was changed.");
      const next = { ...d, status, updated: "Just now" };
      await updatePromo(id, toPromoInput(next) as any);
      _set((_cache ?? []).map((x) => (x.id === id ? next : x)));
    },
    setPaused: async (id: string, paused: boolean) => {
      const d = all.find((x) => x.id === id);
      if (!d) return;
      if (typeof pausePromo !== "function") throw new Error("pausePromo is unavailable in this build — nothing was changed.");
      await pausePromo(id, paused);
      _set((_cache ?? []).map((x) => (x.id === id ? { ...x, paused, updated: "Just now" } : x)));
    },
    remove: async (id: string) => {
      if (typeof deletePromo !== "function") throw new Error("deletePromo is unavailable in this build — nothing was deleted.");
      await deletePromo(id);
      const d = (_cache ?? []).find((x) => x.id === id);
      if (d) logDelete("promo", d as unknown as Record<string, unknown>, { name: d.name });
      _set((_cache ?? []).filter((x) => x.id !== id));
    },
    reload: () => { _cache = null; hydrate(); },
  };
}
